import React, { useState, useEffect, useCallback } from 'react';

import { LinearProgress, styled } from '@mui/material';
import { linearProgressClasses } from '@mui/material/LinearProgress';
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined';

import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import '../stylesheet/homePage.css';

import { fetchUsageData } from '../../../utils/UsageAPI';
import FadeIn from 'react-fade-in';
import { Link } from 'react-router-dom';
import { DashboardCard, StyledSkeleton } from './CommonComponents';
import useProjectStore from '../../ProjectCanvas/useProjectStore';

const COLORS = {
  warning: '#FFA722',
  danger: 'rgba(176, 0, 32, 1)',
  text: 'var(--color-text-primary)',
  link: '#2a9af3',
};

const THRESHOLDS = {
  CRITICAL: 90,
  WARNING: 50,
};

const BorderLinearProgress = styled(LinearProgress, {
  shouldForwardProp: prop => prop !== 'barColor',
})(({ barColor }) => ({
  height: 10,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: 'rgba(0, 0, 0, 0.15)',
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: barColor,
  },
}));

function CreditsUsedCard() {
  const creditBalance = useProjectStore(state => state.creditBalance);
  const billingInitialized = useProjectStore(state => state.billingInitialized);
  const [usedCredits, setUsedCredits] = useState(null);
  const [totalCredits, setTotalCredits] = useState(null);
  const [percentage, setPercentage] = useState(null);
  const [loading, setLoading] = useState(true);

  const calculateUsagePercentage = (used, total) => {
    if (!total) return 0;
    if (total === 0) return 100;
    return Math.round((100 * used) / total);
  };

  const applyCreditsData = useCallback((used, total) => {
    const safeUsed = used ?? 0;
    const safeTotal = total ?? 0;
    setUsedCredits(safeUsed === 0 ? 0.1 : safeUsed);
    setTotalCredits(safeTotal);
    setPercentage(calculateUsagePercentage(safeUsed, safeTotal));
    setLoading(false);
  }, []);

  const fetchBillingData = useCallback(async () => {
    setLoading(true);

    try {
      const usageResponse = await fetchUsageData();
      const remainingCredits =
        creditBalance != null ? creditBalance : useProjectStore.getState().creditBalance;

      if (usageResponse.success && usageResponse.data) {
        const { usedCredits: newUsedCredits, totalCredits: reportedTotalCredits } =
          usageResponse.data;
        const computedTotalCredits =
          remainingCredits != null && newUsedCredits != null
            ? newUsedCredits + remainingCredits
            : reportedTotalCredits;

        applyCreditsData(
          newUsedCredits ?? 0,
          computedTotalCredits || reportedTotalCredits || 0
        );
        return;
      }

      if (remainingCredits != null) {
        applyCreditsData(0, remainingCredits);
        return;
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching billing data:', error);
      setLoading(false);
    }
  }, [applyCreditsData, creditBalance]);

  useEffect(() => {
    if (!billingInitialized) {
      return;
    }

    fetchBillingData();
  }, [billingInitialized, creditBalance, fetchBillingData]);

  const getProgressBarColor = value => {
    if (value >= THRESHOLDS.CRITICAL) return COLORS.danger;
    if (value >= THRESHOLDS.WARNING) return COLORS.warning;
    return COLORS.text;
  };

  const getProgressBar = (used, available) => {
    const usagePercentage = available ? (used / available) * 100 : 0;
    const color = getProgressBarColor(usagePercentage);

    return (
      <div className="progress-bar-outer">
        <div className="text-inside-progress-bar" style={{ textAlign: 'left', width: '100%' }} />
        <BorderLinearProgress variant="determinate" value={usagePercentage} barColor={color} />
      </div>
    );
  };

  const creditsWarningComponent = (
    <div className="actionWarningContainer">
      <FadeIn transitionDuration="1000">
        <div className="actionWarningContent">
          <p className="actionWarningText">
            You are about to run out of credits for this month. To prevent your workflows from being
            disrupted,{' '}
            <Link to="/organization" style={{ color: COLORS.link }}>
              upgrade your plan
            </Link>
          </p>
        </div>
      </FadeIn>
    </div>
  );

  const creditsUsedComponent = usedCredits !== null && totalCredits !== null && (
    <DashboardCard className="cardStyleCredits">
      <CardContent>
        <Typography
          variant="h4"
          component="p"
          className="cardHeader"
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <MonetizationOnOutlinedIcon sx={{ mr: 1 }} /> Credits used this month
        </Typography>
      </CardContent>

      {percentage >= THRESHOLDS.CRITICAL && creditsWarningComponent}

      <CardContent className="cardTopContent">
        <FadeIn transitionDuration="1000">
          <div className="progress-bar-outer">{getProgressBar(usedCredits, totalCredits)}</div>
          <Typography className="cardDescription">
            <strong style={{ color: COLORS.text }}>
              {Math.round(usedCredits).toLocaleString()}
            </strong>{' '}
            credits used ({Math.round(totalCredits - usedCredits).toLocaleString()} credits
            remaining out of{' '}
            <strong style={{ color: COLORS.text }}>{totalCredits.toLocaleString()}</strong>)
          </Typography>
        </FadeIn>
      </CardContent>
    </DashboardCard>
  );

  return loading ? (
    <DashboardCard>
      <CardContent>
        <Typography
          variant="h4"
          component="p"
          className="cardHeader"
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <MonetizationOnOutlinedIcon sx={{ mr: 1 }} /> Credits used this month
        </Typography>
      </CardContent>
      <CardContent className="cardTopContent">
        <StyledSkeleton variant="rectangular" height="10px" margin="16px 0" />
        <StyledSkeleton variant="rectangular" height="20px" width="80%" margin="8px 0" />
        <StyledSkeleton variant="rectangular" height="16px" width="60%" margin="8px 0" />
      </CardContent>
    </DashboardCard>
  ) : (
    creditsUsedComponent
  );
}

export default CreditsUsedCard;
